import React from 'react';
import clsx from 'clsx';
import { SparklesIcon } from '@heroicons/react/16/solid';

import { CARA_SECTION_CAPTION } from './cara-section-node';
import { CARA_SECTION_ATTRIBUTE } from './draft-markers';
import { splitAtFieldReferences } from './field-chips';
import { TEMPLATE_FIELD_MARK_CLASS, SanitizedHtml } from '~/components/sanitized-html';

const emailClassName =
  // dark-mode-exempt: the email previews on white in both themes, as recipients see it.
  'overflow-hidden rounded-lg bg-white text-neutral-900 shadow ring-1 ring-neutral-300';
// dark-mode-exempt: inside the white email preview.
const subjectLineClassName = 'm-0 border-b border-neutral-200 px-6 py-2 text-sm';
// dark-mode-exempt: inside the white email preview.
const subjectLabelClassName = 'text-neutral-500';
const fieldClassName =
  // dark-mode-exempt: inside the white email preview.
  '[&_mark.template-field]:bg-transparent [&_mark.template-field]:text-inherit [&_mark.template-field]:border-b-2 [&_mark.template-field]:border-sky-500';
const caraSectionClassName =
  // dark-mode-exempt: inside the white email preview.
  'rounded-md border border-dashed border-neutral-400 bg-neutral-50 p-2.5 text-neutral-600';

/** A text node's [[label]]s, each as a marked node built from its text alone. */
const markFields = (textNode: Node) => {
  const document = textNode.ownerDocument;
  const parts = splitAtFieldReferences(textNode.textContent ?? '');
  if (!document || !parts.some((part) => part.isFieldReference)) return;
  const nodes = parts.map((part) => {
    if (!part.isFieldReference) return document.createTextNode(part.text);
    const mark = document.createElement('mark');
    mark.className = TEMPLATE_FIELD_MARK_CLASS;
    mark.textContent = part.text;
    return mark;
  });
  (textNode as ChildNode).replaceWith(...nodes);
};

/** An empty paragraph has no height, so it gets the line break the editor shows it with. */
const showBlankLines = (fixedRun: Element) => {
  for (const blankLine of fixedRun.querySelectorAll('p:empty')) {
    blankLine.append(fixedRun.ownerDocument.createElement('br'));
  }
};

type PreviewBlock = { kind: 'fixed'; html: string } | { kind: 'caraSection'; instruction: string };

/**
 * The body as runs of fixed HTML, with each [[label]] marked, and the Cara sections between
 * them. A Cara section nested inside other markup stays in its run as plain text.
 */
export const previewBlocks = (bodyHtml: string): PreviewBlock[] => {
  const body = new DOMParser().parseFromString(bodyHtml, 'text/html').body;
  const blocks: PreviewBlock[] = [];
  let fixedRun = body.ownerDocument.createElement('div');
  const endFixedRun = () => {
    const texts = body.ownerDocument.createTreeWalker(fixedRun, NodeFilter.SHOW_TEXT);
    const textNodes: Node[] = [];
    while (texts.nextNode()) textNodes.push(texts.currentNode);
    textNodes.forEach(markFields);
    showBlankLines(fixedRun);
    if (fixedRun.hasChildNodes()) blocks.push({ kind: 'fixed', html: fixedRun.innerHTML });
    fixedRun = body.ownerDocument.createElement('div');
  };

  for (const node of [...body.childNodes]) {
    const isCaraSection = node instanceof Element && node.hasAttribute(CARA_SECTION_ATTRIBUTE);
    if (isCaraSection) {
      endFixedRun();
      blocks.push({ kind: 'caraSection', instruction: node.textContent?.trim() ?? '' });
    } else {
      fixedRun.append(node);
    }
  }
  endFixedRun();
  return blocks;
};

const CaraSectionPreview = ({ instruction }: { instruction: string }) => (
  <div className={caraSectionClassName}>
    <p className="m-0 flex items-center gap-1.5 text-xs font-semibold">
      <SparklesIcon className="size-4" aria-hidden />
      {CARA_SECTION_CAPTION}
    </p>
    <p className="m-0 mt-1 italic">{instruction}</p>
  </div>
);

export const EmailTemplatePreview = ({
  subject,
  bodyHtml
}: {
  subject: string;
  bodyHtml: string;
}) => {
  const blocks = React.useMemo(() => previewBlocks(bodyHtml), [bodyHtml]);

  return (
    <section aria-label="Preview" className="space-y-3">
      <h2 className="m-0 text-lg font-medium text-neutral-900 dark:text-white">Preview</h2>
      <div className={clsx(emailClassName, fieldClassName)}>
        <p className={subjectLineClassName}>
          <span className={subjectLabelClassName}>Subject:</span>{' '}
          <span className="font-semibold">
            {splitAtFieldReferences(subject).map((part, index) =>
              part.isFieldReference ? (
                <mark key={index} className={TEMPLATE_FIELD_MARK_CLASS}>
                  {part.text}
                </mark>
              ) : (
                part.text
              )
            )}
          </span>
        </p>
        <div className="space-y-3 px-6 py-4 text-sm leading-relaxed [&_p]:my-0">
          {blocks.map((block, index) =>
            block.kind === 'caraSection' ? (
              <CaraSectionPreview key={index} instruction={block.instruction} />
            ) : (
              <SanitizedHtml key={index} html={block.html} className="space-y-3" />
            )
          )}
        </div>
      </div>
    </section>
  );
};
